<template>
  <nav class="bottom-nav" :style="styles" aria-label="Mobile navigation">
    <template v-for="item in items" :key="item.section">

      <!-- Regular item -->
      <a
        v-if="!item.isCta"
        :href="`#${item.section}`"
        class="bottom-nav-item"
        :class="{ active: activeSection === item.section }"
        @click.prevent="navigate(item.section)"
        :aria-label="item.label"
      >
        <div class="nav-icon" v-html="item.icon"></div>
        <span class="nav-label">{{ item.label }}</span>
      </a>

      <!-- Booking CTA — raised centre button -->
      <a
        v-else
        :href="`#${item.section}`"
        class="bottom-nav-item book-cta"
        @click.prevent="navigate(item.section)"
        :aria-label="item.label"
      >
        <div class="book-icon-wrap">
          <div class="nav-icon" v-html="item.icon"></div>
        </div>
        <span class="nav-label book-label">{{ item.label }}</span>
      </a>

    </template>
  </nav>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from '@/composables/useTheme'

const ICON_HOME = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`
const ICON_STAR  = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`
const ICON_CAL   = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`
const ICON_IMG   = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>`
const ICON_PIN   = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>`

export default {
  name: 'BottomNav',
  setup() {
    const { t } = useI18n()
    const { palette } = useTheme()
    const activeSection = ref('home')

    const items = computed(() => [
      { section: 'home',       label: t('nav.home'),       icon: ICON_HOME },
      { section: 'offers',     label: t('nav.offers'),     icon: ICON_STAR },
      { section: 'services',   label: t('nav.services'),   icon: ICON_CAL,  isCta: true },
      { section: 'atmosphere', label: t('nav.atmosphere'), icon: ICON_IMG  },
      { section: 'location',   label: t('nav.location'),   icon: ICON_PIN  },
    ])

    const styles = computed(() => {
      const p = palette.value
      return {
        '--nav-bg':       p?.surface?.card   || '#ffffff',
        '--nav-border':   p?.border?.main    || '#e2e8f0',
        '--nav-active':   p?.brand?.primary  || '#0A1A2F',
        '--nav-inactive': p?.text?.muted     || '#9ca3af',
        '--cta-start':    p?.brand?.overlay  || '#B3D4FF',
        '--cta-end':      p?.brand?.accent   || '#6FA8FF',
        '--cta-shadow':   p?.shadow?.brand   || 'rgba(51,133,255,0.35)',
      }
    })

    const SECTIONS = ['home', 'offers', 'services', 'atmosphere', 'location']

    const updateActive = () => {
      const offset = 120
      let current = 'home'
      for (const id of SECTIONS) {
        const el = document.getElementById(id)
        if (el && el.getBoundingClientRect().top <= offset) current = id
      }
      activeSection.value = current
    }

    const navigate = (section) => {
      activeSection.value = section
      const el = document.getElementById(section)
      if (!el) return
      const top = el.getBoundingClientRect().top + window.scrollY - 72
      window.scrollTo({ top, behavior: 'smooth' })
    }

    onMounted(() => {
      window.addEventListener('scroll', updateActive, { passive: true })
      updateActive()
    })

    onBeforeUnmount(() => {
      window.removeEventListener('scroll', updateActive)
    })

    return { items, styles, activeSection, navigate }
  }
}
</script>

<style scoped>
.bottom-nav {
  display: none;
}

@media (max-width: 768px) {
  .bottom-nav {
    display: flex;
    align-items: stretch;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 998;
    overflow: visible;
    background: var(--nav-bg);
    border-top: 1px solid var(--nav-border);
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.08);
    padding-bottom: env(safe-area-inset-bottom);
    min-height: 56px;
  }
}

.bottom-nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 8px 4px 6px;
  text-decoration: none;
  color: var(--nav-inactive);
  transition: color 0.18s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  cursor: pointer;
  min-height: 56px;
  user-select: none;
}

.bottom-nav-item.active {
  color: var(--nav-active);
}

.nav-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.nav-icon :deep(svg) {
  width: 100%;
  height: 100%;
}

.nav-label {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1;
}

/* ── Raised Book CTA ── */
.book-cta {
  justify-content: flex-end;
  padding-bottom: 6px;
  position: relative;
}

.book-icon-wrap {
  position: absolute;
  top: -16px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--cta-start), var(--cta-end));
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  overflow: hidden;

  /* Glow pulse — mostly still, quick heartbeat every 3s */
  animation: bookPulse 3s ease-in-out infinite;
}

/* Shimmer sweep — diagonal light stripe moving across the circle */
.book-icon-wrap::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    110deg,
    rgba(255, 255, 255, 0)   30%,
    rgba(255, 255, 255, 0.45) 50%,
    rgba(255, 255, 255, 0)   70%
  );
  transform: translateX(-100%);
  animation: bookShimmer 3s ease-in-out infinite;
  border-radius: 50%;
  pointer-events: none;
}

@keyframes bookPulse {
  0%, 70%, 100% {
    box-shadow: 0 4px 18px var(--cta-shadow);
    transform: scale(1);
  }
  80% {
    box-shadow: 0 6px 28px var(--cta-shadow), 0 0 0 7px rgba(255, 255, 255, 0.13);
    transform: scale(1.07);
  }
  90% {
    box-shadow: 0 4px 20px var(--cta-shadow);
    transform: scale(1);
  }
}

@keyframes bookShimmer {
  0%      { transform: translateX(-100%); }
  55%, 100% { transform: translateX(200%); }
}

.book-cta:active .book-icon-wrap {
  animation: none;
  transform: scale(0.94);
  box-shadow: 0 2px 10px var(--cta-shadow);
}

.book-cta .nav-icon {
  color: #fff;
  position: relative; /* above ::before */
  z-index: 1;
}

.book-label {
  color: var(--nav-active);
  font-weight: 700;
}
</style>
